import { useCallback, useRef, useState } from "react";

/**
 * Prevents double-submit on slow networks/devices.
 * Use `run(fn)` for fire-and-forget handlers, or check `isPending` to disable buttons.
 */
export function useAsyncAction() {
  const inFlightRef = useRef(false);
  const [isPending, setIsPending] = useState(false);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (inFlightRef.current) return undefined;
    inFlightRef.current = true;
    setIsPending(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = false;
      setIsPending(false);
    }
  }, []);

  return { run, isPending };
}

/**
 * Module-level key lock for handlers that can't easily share React state
 * (e.g. per-row menu actions). Key should be unique per action+entity.
 */
const globalLocks = new Set<string>();

export async function withActionLock<T>(
  key: string,
  fn: () => Promise<T>
): Promise<T | undefined> {
  const lockKey = String(key || "").trim();
  if (!lockKey) return fn();
  if (globalLocks.has(lockKey)) return undefined;
  globalLocks.add(lockKey);
  try {
    return await fn();
  } finally {
    globalLocks.delete(lockKey);
  }
}

export function isActionLocked(key: string): boolean {
  return globalLocks.has(String(key || "").trim());
}
